import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RoutesRecognized } from '@angular/router';
import { get } from 'lodash';
import { BsModalService } from 'ngx-bootstrap';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { Channel } from 'ideta-library/lib/common/bot';

import { AuthService } from '../auth/auth.service';
import { FeedbackService } from '../feedback/feedback.service';
import { ProtocolDroidService } from '../protocol-droid/services/protocol-droid.service';
import { ToasterService } from '../toaster/toaster.service';
import { IsDirtyService } from './is-dirty.service';

import { ProductUtils } from '../../../utils/products.util';

export type AppPage = keyof typeof BOT_PAGES | 'login' | 'bots-list' | 'projects';

export enum BOT_PAGES {
  analytics = 1,
  settings,
  messaging,
  edition
}

export interface RouterEvent {
  location: AppPage;
  botId?: string;
  channel?: Channel;
  workspace?: string;
  unReachable?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CoreSessionService implements OnDestroy {
  public deletionInProcess: boolean;
  private _routerEvent$: BehaviorSubject<RouterEvent>;
  private previousRouterEvent$: BehaviorSubject<RouterEvent>;
  private routerSub: Subscription;

  constructor(
    private authService: AuthService,
    private router: Router,
    private _isDirty: IsDirtyService,
    private protocolDroidService: ProtocolDroidService,
    private feedbackService: FeedbackService,
    private toasterService: ToasterService,
    private modalService: BsModalService
  ) {
    this._routerEvent$ = new BehaviorSubject(null);
    this.previousRouterEvent$ = new BehaviorSubject(null);
    this.routerSub = this.router.events.pipe(filter(event => event instanceof RoutesRecognized)).subscribe((event: RoutesRecognized) => {
      // For pages in the Idechat Module, there are 3 levels of nodes to browse (ROOT(AppComponent)/Idechat/{target})
      // For pages in the App Module, there is 1 level to browse (ROOT(AppComponent)/{target}))
      const routerNode: Partial<ActivatedRouteSnapshot> = get(
        event,
        'state.root.firstChild.firstChild.firstChild',
        get(event, 'state.root.firstChild', {})
      );

      const routerEvent: RouterEvent = {
        location: get(routerNode, 'url[0].path') as AppPage,
        botId: get(routerNode, 'params.botId'),
        channel: get(routerNode, 'queryParams.channel'),
        workspace: get(routerNode, 'params.workspaceId')
      };

      routerEvent.unReachable = BOT_PAGES[routerEvent.location] && !ProductUtils.accessPages(routerEvent.location);

      this.previousRouterEvent$.next(this._routerEvent$.value);
      if (this._isDirty.getIsDirty()) {
        setTimeout(() => {
          this.modalService.onHidden.pipe(take(1)).subscribe(() => {
            if (this.feedbackService.messageRef.content.isConfirmed) {
              this._isDirty.setIsDirty(false);
              this._routerEvent$.next(routerEvent);
            }
          });
        }, 100);
      } else {
        this._routerEvent$.next(routerEvent);
      }
    });
  }

  // Note about routerEvent$ and unReachableRouterEvent$:
  // We do not want any session service to start other than userSessionService
  // when the location in unreachable (out of the current product's scope).
  // Thats why regular session services subscribe to routerEvent$
  // and userSessionService subscribes to unreachableRouterEvent$
  // --------------------------------------------------------------------------
  get routerEvent$(): Observable<RouterEvent> {
    return this._routerEvent$.pipe(filter(event => !!event && !event.unReachable));
  }

  get unReachableRouterEvent$(): Observable<RouterEvent> {
    return this._routerEvent$.pipe(filter(event => !!event));
  }
  // --------------------------------------------------------------------------

  get location(): AppPage {
    return get(this._routerEvent$, 'value.location', null);
  }

  endSession(): void {
    this.deletionInProcess = false;
    this._routerEvent$.next({ location: 'login' });
  }

  ngOnDestroy(): void {
    if (this.routerSub) this.routerSub.unsubscribe();
  }

  public handleAccessDenied(scope: 'bot' | 'page' | 'workspace' | 'product'): void {
    if (!this.authService.getCurrentUser()) {
      this.authService.navigateToLogin();
    } else if (scope === 'workspace' || scope === 'product' || BOT_PAGES[this.location]) {
      this.toasterService.showToast('warning', {
        message: this.protocolDroidService.translate('idechat.bots-list.access-denied-' + scope, 'Access denied for this page')
      });
      this._routerEvent$.next({ location: 'projects' });
      this.router.navigate(['/projects']);
    }
  }
}
